import { useState } from "react";
import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import { architectureFlow, technologyDetails } from "@/mocks/technology";

export default function Technology() {
  const [active, setActive] = useState(0);

  return (
    <>
      <Seo
        title="EV Retrofit Technology | Powertrain, Battery, BMS & Charging"
        description="Explore EV retrofit technology: retrofit architecture, electric powertrain, battery pack, BMS, controller, DC/DC, charging, connectivity, safety and diagnostics."
        keywords="EV retrofit technology, battery management system, electric powertrain, EV charging ecosystem"
        path="/technology"
      />
      <PageHero
        eyebrow="Technology"
        breadcrumb="Technology"
        title="The Engineering Behind an Electric Retrofit"
        description="A retrofit is a system, not a single component. Powertrain, energy storage, power electronics, control, charging and safety are engineered together around the vehicle."
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Retrofit Architecture"
              title="How the Systems Connect"
              description="Select a layer to understand how it fits into the retrofit architecture. This is a conceptual system view — the actual configuration is defined during engineering."
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <div className="flex flex-col gap-2">
                {architectureFlow.map((step, index) => {
                  const isActive = index === active;
                  return (
                    <button
                      key={step.key}
                      type="button"
                      onClick={() => setActive(index)}
                      aria-pressed={isActive}
                      className={`flex cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-colors ${
                        isActive
                          ? "border-primary-400 bg-primary-50"
                          : "border-background-200 bg-background-100 hover:border-primary-300"
                      }`}
                    >
                      <span
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${
                          isActive ? "bg-primary-500 text-foreground-950" : "bg-background-50 text-foreground-800"
                        }`}
                      >
                        <i className={`${step.icon} text-xl`} />
                      </span>
                      <span className="flex-1">
                        <span className="block font-label text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground-500">
                          Layer {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="block text-sm font-semibold text-foreground-950">
                          {step.label}
                        </span>
                      </span>
                      {index < architectureFlow.length - 1 ? (
                        <i className="ri-arrow-down-line text-foreground-400" />
                      ) : (
                        <i className="ri-check-line text-primary-600" />
                      )}
                    </button>
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-7">
              <div className="flex h-full flex-col justify-center rounded-lg border border-background-200 bg-foreground-950 p-7 md:p-10">
                <span className="flex h-14 w-14 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                  <i className={`${architectureFlow[active].icon} text-3xl`} />
                </span>
                <p className="mt-6 font-label text-xs font-semibold uppercase tracking-[0.2em] text-primary-300">
                  Layer {String(active + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-2 text-2xl font-bold text-background-50">{architectureFlow[active].label}</h3>
                <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
                  {architectureFlow[active].description}
                </p>
                <p className="mt-6 rounded-md border border-background-50/[0.12] bg-background-50/[0.05] p-4 text-xs leading-relaxed text-background-300">
                  <i className="ri-information-line mr-2" />
                  Component selection and system configuration depend on the vehicle and are confirmed
                  after technical assessment.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="System Detail"
              title="Technology Areas in a Retrofit"
              description="Each area below is part of the engineering scope. Specific values, ratings and part selections are configuration dependent and decided per vehicle."
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {technologyDetails.map((item, index) => (
              <Reveal key={item.key} delay={index * 0.04}>
                <article className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                  <ul className="mt-3 space-y-2">
                    {item.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-foreground-600">
                        <i className="ri-subtract-line mt-0.5 text-primary-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col gap-3 sm:flex-row">
              <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="lg" icon="ri-flashlight-line">
                Check Retrofit Eligibility
              </ActionButton>
              <ActionButton to="/contact" variant="outline" size="lg" icon="ri-chat-3-line">
                Discuss Your Requirements
              </ActionButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}